import JSZip from 'jszip'
import { saveAs } from 'file-saver'

// 定义资源项目的接口
export interface ResourceItem {
  resource: string;
  image_id?: string;
  [key: string]: any; // 允许其他属性存在
}

// 定义配置选项的接口
export interface DownloadOptions {
  baseUrl?: string;
  timeout?: number;
}

/**
 * 并发控制函数，用于限制并发执行的任务数
 * @param tasks - 任务列表，每个任务是一个返回Promise的函数
 * @param maxConcurrency - 最大并发数
 * @returns 所有任务完成的Promise
 */
async function concurrentTasks<T>(tasks: (() => Promise<T>)[], maxConcurrency: number): Promise<T[]> {
  const results: T[] = [];
  const executing: Promise<void>[] = [];
  
  for (const task of tasks) {
    // 创建一个任务执行函数
    const executeTask = async () => {
      const result = await task();
      results.push(result);
    };
    
    // 开始执行任务
    const taskPromise = executeTask();
    executing.push(taskPromise);
    
    // 当任务完成后，从执行队列中移除
    const cleanup = () => {
      const index = executing.indexOf(taskPromise);
      if (index >= 0) {
        executing.splice(index, 1);
      }
    };
    taskPromise.then(cleanup).catch(cleanup);
    
    // 如果当前执行的任务数达到最大并发数，等待其中一个任务完成
    if (executing.length >= maxConcurrency) {
      await Promise.race(executing);
    }
  }
  
  // 等待所有任务完成
  await Promise.all(executing);
  return results;
}

/**
 * 将资源数据打包成 zip 文件并下载
 * @param data - 包含资源信息的数组
 * @param filename - 下载的 zip 文件名
 * @param options - 配置选项
 * @throws 下载失败时抛出错误
 */
export async function downloadResourcesAsZip(
  data: ResourceItem[],
  filename: string = 'resources.zip',
  options: DownloadOptions = {}
): Promise<void> {
  const {
    baseUrl = '',
    timeout = 10000
  } = options
  
  const zip = new JSZip()
  
  try {
    // 将数据保存为 JSON 文件
    zip.file('data.json', JSON.stringify(data, null, 2))
    
    // 公共图片代理地址列表（只保留一个主代理和备用占位图）
    const proxyList = [
      'https://images.weserv.nl/?url=',
      'https://images.all-free-download.com/images/graphiclarge/placeholder_318724.jpg' // 备用占位图
    ];
    // 收集需要下载的图片任务
    const imageTasks: Array<() => Promise<{ fileName: string; blob: Blob }>> = [];
    
    // 最大并发数
    const MAX_CONCURRENCY = 4;
    
    // 添加图片资源
    data.forEach((item, index) => {
      const target = item.resource;
      if (target) {
        // 创建图片下载任务
        const downloadTask = async (): Promise<{ fileName: string; blob: Blob }> => {
          const imageUrl = `${baseUrl}/${target}`
          const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : `${baseUrl}/${target}`;
          let blob: Blob | null = null;
          let fetchError: Error | null = null;
          
          // 尝试使用每个代理，直到成功获取图片或所有代理都失败
          for (let proxyIndex = 0; proxyIndex < proxyList.length; proxyIndex++) {
            try {
              // 添加超时处理
              const controller = new AbortController()
              const id = setTimeout(() => controller.abort(), timeout)
              
              const proxyUrl = proxyList[proxyIndex];
              
              // 最后一个是备用占位图，直接使用
              const proxiedImageUrl = proxyIndex === proxyList.length - 1 
                ? proxyUrl 
                : `${proxyUrl}${encodeURIComponent(fullImageUrl)}`;
              
              const response = await fetch(proxiedImageUrl, {
                signal: controller.signal,
                mode: 'cors',
                credentials: 'omit'
              })
              clearTimeout(id)
              
              if (!response.ok) {
                throw new Error(`Proxy ${proxyIndex + 1} failed to fetch image (HTTP ${response.status}): ${proxiedImageUrl}`)
              }
              
              blob = await response.blob();
              console.log(`Successfully fetched image using proxy ${proxyIndex + 1}: ${fullImageUrl}`)
              break; // 成功获取图片，跳出代理循环
            } catch (error) {
              fetchError = error instanceof Error ? error : new Error('Unknown error');
              console.error(`Proxy ${proxyIndex + 1} failed for image ${fullImageUrl}:`, fetchError)
              
              // 如果是最后一个代理也失败了，才抛出错误
              if (proxyIndex === proxyList.length - 1) {
                throw fetchError;
              }
            }
          }
          
          if (!blob) {
            throw new Error(`All proxies failed for image: ${fullImageUrl}`);
          }
          
          // 提取文件名
          const fileExtension = target.split('.').pop() || 'jpg'
          const fileName = `${item.image_id || `未知id-${index}`}.${fileExtension}`
          
          return { fileName, blob };
        };
        
        imageTasks.push(downloadTask);
      }
    });
    
    // 并发下载图片
    const imageResults = await concurrentTasks(imageTasks, MAX_CONCURRENCY);
    
    // 将下载的图片添加到zip中
    imageResults.forEach(({ fileName, blob }) => {
      zip.file(fileName, blob);
    });
    
    // 生成并下载 zip 文件
    const zipBlob = await zip.generateAsync({ type: 'blob' })
    // 使用file-saver下载ZIP文件
    saveAs(zipBlob, filename)
    
    console.log('Download completed successfully!')
  } catch (error) {
    console.error('Error creating zip file:', error)
    throw error
  }
}
